import axios from "axios";
import moment from "moment";
import "moment/locale/ar-ly";

// --- Types ---

export type SitecoreImageField = {
  value: {
    src: string;
    alt: string;
    width: string;
    height: string;
  };
};

export type VotingSectionLayout = {
  id: string;
  url?: string;
  name?: string;
  displayName?: string;
  fields: {
    Title: {
      value: string;
    };
  };
};

export type VotingOption = {
  id: string;
  url?: string;
  name?: string;
  displayName?: string;
  fields: {
    Image?: SitecoreImageField;
    Title?: { value: string };
    Description?: { value: string };
  };
};

export type VotingSectionFields = {
  VotingSectionLayout: VotingSectionLayout;
  SubmitButtonText: { value: string };
  SuccessToastTitle: { value: string };
  SuccessToastDescription: { value: string };
  ErrorToastTitle: { value: string };
  ErrorToastDescription: { value: string };
  VotingOptions: VotingOption[];
  VotingOptionsTitle?: { value: string };
  Title: { value: string };
  SubTitle: { value: string };
  CalendarIcon: SitecoreImageField;
  Description: { value: string };
  Date: { value: string };
  SectionBackgroundColor: { value: string };
  SectionTemplate?: {
    id?: string;
    url?: string;
    name?: string;
    displayName?: string;
    fields: {
      Title: {
        value: string;
      };
    };
  };
  ScrollToText?: { value: string };
  isScrollAllowed?: { value: boolean };
};

export type VotingPageMetaData = {
  pageTitle?: { value?: string };
  itemId?: string;
  itemPath?: string;
};

export type VotingSectionCo = {
  id: string;
  displayName: string;
  fields: VotingSectionFields;
};

export type SubmitVoteParams = {
  pageId?: string;
  pageName: string;
  voteIds: string[];
  language: string;
  commission?: string;
  captchaResponse: string;
};

// --- Layout constants ---

export const VOTING_LAYOUT = {
  IMAGE_CARDS: "ImageCards",
  CHECKBOX_LABELS: "CheckboxLabels",
  RADIO_BUTTONS: "RadioButtons",
  POOL_BUTTONS: "PoolButtons",
} as const;

export type VotingLayoutKey = (typeof VOTING_LAYOUT)[keyof typeof VOTING_LAYOUT];

export type VotingPollCountsBySection = Record<string, Record<string, number>>;

export type TemplateSectionWithVoting = {
  id: string;
  fields?: VotingSectionFields & {
    SectionTemplate?: {
      fields?: {
        Title?: { value: string };
      };
    };
  };
};

const EMPTY_DATE = "0001-01-01T00:00:00Z";
const SITECORE_REST_BASE = process.env.NEXT_PUBLIC_SITECORE_REST?.trim() ?? "";
const SUBMIT_VOTE_ENDPOINT = `${SITECORE_REST_BASE}/api/Vote/Submit`;
const GET_OPTIONS_COUNT_ENDPOINT = `${SITECORE_REST_BASE}/api/Vote/GetOptionsCount`;

export type VoteOptionCountResponse = {
  OptionId: string;
  TotalVoteCount: number;
};

export const formatSitecoreGuid = (id?: string): string => {
  if (!id) return "";
  const clean = id.replace(/[{}]/g, "").trim();
  if (!clean) return "";
  return `{${clean.toUpperCase()}}`;
};

const normalizeGuid = (id?: string): string => id?.replace(/[{}]/g, "").trim().toUpperCase() ?? "";

const formatOptionIdForVoteApi = (id: string): string => normalizeGuid(id);

export const getVotingSectionLayoutKey = (fields?: VotingSectionFields): string | undefined =>
  fields?.VotingSectionLayout?.fields?.Title?.value;

export const isPoolButtonsLayout = (layoutKey?: string): boolean =>
  layoutKey === VOTING_LAYOUT.POOL_BUTTONS;

export const isVotingSection = (section?: TemplateSectionWithVoting): boolean =>
  section?.fields?.SectionTemplate?.fields?.Title?.value === "VotingSection";

export const fetchVotingPollCounts = async (
  optionIds: string[],
  fallbackCounts?: Record<string, number>
): Promise<Record<string, number>> => {
  const zeroCounts = Object.fromEntries(optionIds.map((id) => [id, 0]));
  const fallback = fallbackCounts ?? zeroCounts;

  if (!optionIds.length || !SITECORE_REST_BASE) {
    return fallback;
  }

  try {
    const { data } = await axios.post<VoteOptionCountResponse[]>(
      GET_OPTIONS_COUNT_ENDPOINT,
      { optionsId: optionIds.map(formatOptionIdForVoteApi).filter(Boolean) },
      { headers: { "Content-Type": "application/json" } }
    );

    if (!Array.isArray(data)) {
      return fallback;
    }

    const countsByNormalizedId = Object.fromEntries(
      data.map((item) => [normalizeGuid(item.OptionId), item.TotalVoteCount ?? 0])
    );

    return Object.fromEntries(
      optionIds.map((id) => [id, countsByNormalizedId[normalizeGuid(id)] ?? 0])
    );
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("GetOptionsCount error:", error.response?.data || error.message);
    }
    return fallback;
  }
};

export const resolveVotingPollCountsForTemplate = async (
  templateSections?: TemplateSectionWithVoting[]
): Promise<VotingPollCountsBySection> => {
  if (!templateSections?.length) return {};

  const pollCountsBySection: VotingPollCountsBySection = {};

  await Promise.all(
    templateSections.map(async (section) => {
      if (!isVotingSection(section) || !isPoolButtonsLayout(getVotingSectionLayoutKey(section.fields))) {
        return;
      }

      const optionIds = (section.fields?.VotingOptions ?? []).map((option) => option.id).filter(Boolean);
      if (!optionIds.length) return;

      pollCountsBySection[section.id] = await fetchVotingPollCounts(optionIds);
    })
  );

  return pollCountsBySection;
};

export const formatVotingSectionDate = (
  dateValue: string,
  locale: string
): { datePart: string; timePart: string } | null => {
  if (!dateValue || dateValue === EMPTY_DATE || !moment(dateValue).isValid()) {
    return null;
  }

  const momentLocale = locale === "ar" ? "ar-ly" : locale || "en";
  const parsed = moment(dateValue).locale(momentLocale);

  return {
    datePart: parsed.format(locale === "ar" ? "D MMMM YYYY" : "MMMM D, YYYY"),
    timePart: parsed.format("h:mmA"),
  };
};

export const submitVote = async ({
  pageId,
  pageName,
  voteIds,
  language,
  commission = "moc",
  captchaResponse,
}: SubmitVoteParams) =>
  axios.post(
    SUBMIT_VOTE_ENDPOINT,
    {
      pageId: formatSitecoreGuid(pageId),
      pageName,
      voteIds: voteIds.map(formatSitecoreGuid).filter(Boolean),
      language,
      commission,
      captchaResponse,
    },
    { headers: { "Content-Type": "application/json" } }
  );
